// Access gating, dedup and autosave. Participant-side helpers + investigator CRUD.
import { supabase, isConfigured } from './supabase';
import { studies, studyBySlug } from '../studies/index.js';

const LS = 'kapstat_';

// ---------- participant: settings, codes, dedup ----------
export async function getStudySettings(slug) {
  const fallback = { gated: false, single_response: true, eligible_roles: null };
  if (!isConfigured) return fallback;
  const { data, error } = await supabase.from('study_settings').select('gated,single_response,eligible_roles').eq('study_slug', slug).maybeSingle();
  if (error) return fallback; // e.g. column not present yet (run 0004)
  return data || fallback;
}

// Studies a given role can self-enter (open or eligible for that role; excludes invite-only).
export async function eligibleStudies(role, excludeSlug) {
  const map = {};
  if (isConfigured) {
    try {
      const { data } = await supabase.from('study_settings').select('study_slug,eligible_roles,gated');
      (data || []).forEach((s) => { map[s.study_slug] = s; });
    } catch { /* */ }
  }
  return studies.filter((s) => {
    if (s.slug === excludeSlug) return false;
    const st = map[s.slug];
    if (st && st.gated) return false;
    const roles = st && st.eligible_roles;
    if (!roles || roles.length === 0) return true;
    return roles.includes(role);
  });
}

export async function redeemCode(code, slug) {
  if (!isConfigured) return { ok: true, group_tag: null };
  const { data, error } = await supabase.rpc('redeem_code', { p_code: (code || '').trim(), p_study: slug });
  if (error) return { ok: false, reason: String(error.message || error) };
  const row = Array.isArray(data) ? data[0] : data;
  return row || { ok: false, reason: 'invalid' };
}

export async function hasFinalSubmission(slug, finalPhase) {
  if (!isConfigured) return false;
  const { data } = await supabase.from('submission').select('id').eq('study_slug', slug).eq('phase', finalPhase).limit(1);
  return !!(data && data.length);
}

// ---------- localStorage: autosave (work-in-progress) + flags ----------
const wipKey = (uid, slug) => `${LS}wip:${uid}:${slug}`;
const doneKey = (uid, slug) => `${LS}done:${uid}:${slug}`;
const codeKey = (uid, slug) => `${LS}code:${uid}:${slug}`;

export function loadWip(uid, slug) { try { return JSON.parse(localStorage.getItem(wipKey(uid, slug)) || 'null'); } catch { return null; } }
export function saveWip(uid, slug, data) { try { localStorage.setItem(wipKey(uid, slug), JSON.stringify(data)); } catch { /* quota */ } }
export function clearWip(uid, slug) { try { localStorage.removeItem(wipKey(uid, slug)); } catch { /* */ } }
export function markDone(uid, slug) { try { localStorage.setItem(doneKey(uid, slug), '1'); } catch { /* */ } }
export function isDoneLocal(uid, slug) { try { return localStorage.getItem(doneKey(uid, slug)) === '1'; } catch { return false; } }
export function saveCodeFlag(uid, slug, tag) { try { localStorage.setItem(codeKey(uid, slug), tag || '1'); } catch { /* */ } }
export function getCodeFlag(uid, slug) { try { return localStorage.getItem(codeKey(uid, slug)); } catch { return null; } }
const screenKey = (uid, slug) => `${LS}screen:${uid}:${slug}`;
export function saveScreenFlag(uid, slug, role) { try { localStorage.setItem(screenKey(uid, slug), role || '1'); } catch { /* */ } }
export function getScreenFlag(uid, slug) { try { return localStorage.getItem(screenKey(uid, slug)); } catch { return null; } }

// ---------- investigator: manage rules + codes (RLS-gated) ----------
export async function getSettingsInv(slug) {
  const { data } = await supabase.from('study_settings').select('*').eq('study_slug', slug).maybeSingle();
  return data || { study_slug: slug, gated: false, single_response: true };
}
export async function setSettings(slug, patch) {
  const { error } = await supabase.from('study_settings')
    .upsert({ study_slug: slug, ...patch, updated_at: new Date().toISOString() }, { onConflict: 'study_slug' });
  if (error) throw error;
}
export async function listCodes(slug) {
  const { data, error } = await supabase.from('access_code').select('*').eq('study_slug', slug).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}
// Invite codes carry the study code as a prefix (e.g. KS01-7K2QP) so a student can
// see which study a code belongs to. The 5-char body is drawn from an unambiguous
// alphabet (no I/O/0/1) — case-insensitive and safe to print on a QR/handout.
export function suggestCode(prefix) {
  const s = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let r = ''; for (let i = 0; i < 5; i++) r += s[Math.floor(Math.random() * s.length)];
  return ((prefix || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8) || 'KS') + '-' + r;
}
// Inserts an access code. Auto-generated codes are non-repeatable: on the (rare)
// collision with the table's UNIQUE(code) constraint we regenerate and retry. An
// explicitly typed code surfaces the duplicate error so the user can pick another.
export async function createCode(slug, { code, label, group_tag, singleUse }) {
  const studyCode = studyBySlug(slug)?.code || 'KS';
  const explicit = !!(code && code.trim());
  let attempt = explicit ? code.trim().toUpperCase() : suggestCode(studyCode);
  for (let i = 0; i < 6; i++) {
    const { data, error } = await supabase.from('access_code')
      .insert({ code: attempt, study_slug: slug, label: label || null, group_tag: group_tag || null, max_uses: singleUse ? 1 : null })
      .select().single();
    if (!error) return data;
    const dup = /duplicate|unique/i.test(String(error.message || error));
    if (dup && !explicit && i < 5) { attempt = suggestCode(studyCode); continue; }
    throw error;
  }
}
export async function setCodeActive(id, active) {
  const { error } = await supabase.from('access_code').update({ active }).eq('id', id);
  if (error) throw error;
}
export function shareLink(slug, code) {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/study/${slug}?c=${encodeURIComponent(code)}`;
}
